import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { useCart } from "@/contexts/CartContext";
import { http, resolveImage } from "@/lib/api";
import { Trash2, ArrowRight, Gift, X } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, removeAt, setQty, count } = useCart();
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const nav = useNavigate();
  const [search] = useSearchParams();
  const [giftCode, setGiftCode] = useState(() => localStorage.getItem("stc_gift_code") || search.get("gift") || "");
  const [giftPreview, setGiftPreview] = useState(null);
  const [giftErr, setGiftErr] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!items.length) { setQuote(null); return; }
    const payload = items.map(it => ({
      artwork_id: it.artwork_id, variant_key: it.variant_key,
      quantity: it.quantity, personal_text: it.personal_text || null,
    }));
    setQuoting(true);
    http.post("/cart/quote", payload).then(({ data }) => setQuote(data)).catch(() => setQuote(null)).finally(() => setQuoting(false));
  }, [items]);

  useEffect(() => {
    if (!giftCode || !quote) { setGiftPreview(null); return; }
    setApplying(true);
    http.post("/gift-cards/preview", { code: giftCode, cart_total: quote.total })
      .then(({ data }) => { setGiftPreview(data); setGiftErr(""); localStorage.setItem("stc_gift_code", giftCode); })
      .catch(err => { setGiftPreview(null); setGiftErr(err.response?.data?.detail || "Invalid"); })
      .finally(() => setApplying(false));
  }, [giftCode, quote]);

  const grandTotal = quote ? Math.max(0, (quote.total || 0) - (giftPreview?.applies || 0)) : 0;

  return (
    <Layout>
      <section className="bg-black pt-24 pb-24 min-h-[60vh]">
        <div className="max-w-[1280px] mx-auto px-6 md:px-12">
          <h1 className="text-grunge text-5xl md:text-6xl text-[#F5F2EB] leading-[0.95] mb-12">CARRY IT WITH YOU.</h1>

          {items.length === 0 ? (
            <div className="border border-white/10 p-12 text-center">
              <p className="font-body italic text-xl text-[#A3A3A3] mb-6">Your cart is empty. The next truth is waiting.</p>
              <Link to="/shop" data-testid="cart-shop-link" className="btn-copper">Shop the Collection <ArrowRight size={16}/></Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
                {items.map((it, i) => (
                  <div key={i} data-testid={`cart-item-${i}`} className="flex gap-5 border border-white/10 p-4 bg-[#0a0a0a]">
                    <img src={resolveImage(it.main_image)} alt={it.title} className="w-28 h-28 object-cover" />
                    <div className="flex-1">
                      <div className="text-grunge text-xl text-[#F5F2EB]">{it.title}</div>
                      <div className="font-ui text-[11px] tracking-[0.18em] uppercase text-[#B8915A] mt-1">{it.variant_label}</div>
                      {it.personal_text && (
                        <div className="font-body italic text-sm text-[#A3A3A3] mt-2 line-clamp-2">“{it.personal_text}”</div>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-white/15">
                          <button onClick={() => setQty(i, it.quantity - 1)} className="px-3 py-1.5 font-ui">−</button>
                          <div className="px-4 font-ui text-sm">{it.quantity}</div>
                          <button onClick={() => setQty(i, it.quantity + 1)} className="px-3 py-1.5 font-ui">+</button>
                        </div>
                        <div className="font-ui text-[#B8915A] font-semibold">${(it.price * it.quantity).toFixed(2)}</div>
                      </div>
                    </div>
                    <button data-testid={`cart-remove-${i}`} onClick={() => removeAt(i)} className="text-[#737373] hover:text-red-400 self-start p-1">
                      <Trash2 size={16}/>
                    </button>
                  </div>
                ))}
              </div>

              <aside className="border border-white/10 p-6 bg-[#0a0a0a] h-fit sticky top-28">
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#B8915A] uppercase mb-4">Order Summary</div>
                <div className="space-y-2 font-body text-[#F5F2EB] mb-4">
                  <div className="flex justify-between"><span>Items</span><span>{count}</span></div>
                  <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">{quoting && !quote ? "…" : `$${(quote?.subtotal ?? 0).toFixed(2)}`}</span></div>
                  <div className="flex justify-between"><span>Shipping</span><span data-testid="cart-shipping">{quoting && !quote ? "…" : (quote?.shipping ?? 0) === 0 ? "Free / Calculated at checkout" : `$${quote.shipping.toFixed(2)}`}</span></div>
                  {giftPreview && (
                    <div className="flex justify-between text-[#C9A055]">
                      <span className="inline-flex items-center gap-1.5"><Gift size={13}/> Gift card</span>
                      <span data-testid="cart-gift-discount">-${giftPreview.applies.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Gift card code field */}
                <div className="border-t border-white/10 pt-4 pb-4">
                  <label className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#9a9690] block mb-2">Gift card code</label>
                  <div className="flex gap-2">
                    <input
                      data-testid="cart-gift-input"
                      type="text"
                      value={giftCode}
                      onChange={e => setGiftCode(e.target.value.toUpperCase())}
                      placeholder="XXXX-XXXX-XXXX-XXXX"
                      className="stc-input font-mono text-sm flex-1"
                    />
                    {giftCode && (
                      <button
                        type="button"
                        data-testid="cart-gift-clear"
                        onClick={() => { setGiftCode(""); setGiftPreview(null); setGiftErr(""); localStorage.removeItem("stc_gift_code"); }}
                        className="px-3 border border-white/10 text-[#9a9690] hover:text-[#F5F2EB] hover:border-white/30"
                      >
                        <X size={14}/>
                      </button>
                    )}
                  </div>
                  {applying && <div className="font-ui text-[10px] text-[#9a9690] mt-2">Checking…</div>}
                  {giftPreview && (
                    <div className="font-ui text-[10px] text-emerald-400 mt-2" data-testid="cart-gift-applied">
                      ${giftPreview.applies.toFixed(2)} will be applied · ${giftPreview.remaining_after.toFixed(2)} remaining
                    </div>
                  )}
                  {giftErr && !giftPreview && (
                    <div className="font-ui text-[10px] text-red-400 mt-2" data-testid="cart-gift-error">{giftErr}</div>
                  )}
                </div>

                <div className="border-t border-white/10 pt-4 flex justify-between items-baseline">
                  <span className="font-ui text-[11px] tracking-[0.22em] uppercase text-[#737373]">Total</span>
                  <span data-testid="cart-total" className="text-grunge text-3xl text-[#F5F2EB]">{quoting && !quote ? "—" : `$${grandTotal.toFixed(2)}`}</span>
                </div>
                <button data-testid="cart-checkout-btn" onClick={() => nav("/checkout")} className="btn-copper w-full mt-6 justify-center">Proceed to Checkout <ArrowRight size={16}/></button>
                <Link to="/shop" className="block text-center mt-3 btn-ghost">Continue Shopping</Link>
              </aside>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
